import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { getLocalCache, setLocalCache } from '../../utils/localCache';
import { slugify } from '../../utils/slugify';
import { supabase } from '../../utils/supabase';

const CACHE_KEY = 'home_member_clubs_v1';
const CACHE_TTL_MS = 15 * 60 * 1000; // 15m

const INVALID_HOME_CLUBS = new Set(['', 'No Home Golf Club', 'No Golf Club']);

function countyFlagUrl(county) {
  if (!county) return null;
  const slugCounty = slugify(county);
  if (!slugCounty) return null;
  return `https://api.golflists.com/storage/v1/object/public/county-flags/${slugCounty}.webp`;
}

export default function HomeMemberClubs() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cards, setCards] = useState([]);

  useEffect(() => {
    const cached = getLocalCache(CACHE_KEY, CACHE_TTL_MS);
    if (Array.isArray(cached?.cards)) {
      setCards(cached.cards);
      setIsLoading(false);
    }

    let cancelled = false;
    const load = async () => {
      setError(null);
      setIsLoading(true);
      try {
        // Pull member-declared home clubs (recent first). No aggregation: show a card stream.
        const { data: profiles, error: profilesError } = await supabase
          .from('user_profile')
          .select('home_golf_club, created_at')
          .order('created_at', { ascending: false })
          .limit(250);
        if (profilesError) throw profilesError;

        const clubStream = (profiles || [])
          .map((p) => ({
            club: (p?.home_golf_club ?? '').trim(),
            created_at: p?.created_at ?? null,
          }))
          .filter((p) => p.club && !INVALID_HOME_CLUBS.has(p.club));

        if (clubStream.length === 0) {
          if (!cancelled) setCards([]);
          return;
        }

        const uniqueClubNames = Array.from(new Set(clubStream.map((p) => p.club)));

        const { data: clubs, error: clubsError } = await supabase
          .from('golf_clubs')
          .select(
            'club_uid, club_name, slug_club_name, club_country, club_county, number_holes, club_formation',
          )
          .in('club_name', uniqueClubNames)
          .limit(300);
        if (clubsError) throw clubsError;

        const byName = new Map();
        for (const c of clubs || []) byName.set(c.club_name, c);

        const nextCards = clubStream.map((p, idx) => {
          const c = byName.get(p.club);
          if (!c) {
            return {
              key: `${slugify(p.club)}_${idx}`,
              course_name: p.club,
              slug: slugify(p.club),
              created_at: p.created_at,
              course_country: null,
              course_county: null,
              county_rank: null,
              country_rank: null,
              course_style: null,
              architect: null,
              weekend_green_fee: null,
              hasCourse: false,
            };
          }
          return {
            key: `${c.club_uid || c.club_name}_${idx}`,
            course_name: c.club_name,
            slug: c.slug_club_name || slugify(c.club_name),
            created_at: p.created_at,
            course_country: c.club_country,
            course_county: c.club_county,
            number_holes: c.number_holes ?? null,
            club_formation: c.club_formation ?? null,
            county_rank: null,
            country_rank: null,
            course_style: null,
            architect: null,
            weekend_green_fee: null,
            hasCourse: true,
          };
        });

        if (!cancelled) {
          setCards(nextCards);
          setLocalCache(CACHE_KEY, { cards: nextCards });
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load member clubs');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="bg-white py-6 sm:py-10">
      <div className="w-11/12 mx-auto">
        <div className="bg-white rounded-2xl shadow-xl ring-1 ring-slate-200/80 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">
                  Member clubs
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  A live feed of where members are joining from.
                </p>
              </div>
              <Link
                to="/lists/golf-clubs/club-memberships"
                className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
              >
                Browse clubs →
              </Link>
            </div>

            {error ? (
              <p className="mt-4 text-sm text-red-600">{error}</p>
            ) : isLoading && cards.length === 0 ? (
              <div className="mt-5 flex gap-3 overflow-x-auto pb-2 animate-pulse">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-28 w-[260px] shrink-0 rounded-xl bg-slate-100" />
                ))}
              </div>
            ) : (
              <div className="mt-5 -mx-6 px-6 overflow-x-auto">
                <div className="flex gap-3 pb-2 snap-x snap-mandatory">
                  {cards.map((c) => {
                    const href = c.hasCourse ? `/lists/golf-clubs/${c.slug}` : null;
                    const Wrapper = href ? Link : 'div';
                    const wrapperProps = href ? { to: href } : {};
                    return (
                      <Wrapper
                        key={c.key}
                        {...wrapperProps}
                        className={`snap-start w-[260px] sm:w-[300px] shrink-0 group rounded-xl border border-slate-200 p-4 transition-colors ${
                          href ? 'hover:border-slate-400 hover:bg-slate-50' : 'bg-slate-50/40'
                        }`}
                      >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate group-hover:underline">
                          {c.course_name}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-600 truncate">
                          {c.course_county ? (
                            <>
                              {c.course_county}
                              {c.course_country ? `, ${c.course_country}` : ''}
                            </>
                          ) : (
                            <>Member-declared club</>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        {c.country_rank != null && (
                          <span className="text-xxs font-mono text-slate-500">
                            #{c.country_rank} {c.course_country}
                          </span>
                        )}
                        {c.county_rank != null && (
                          <span className="text-xxs font-mono text-slate-500">
                            #{c.county_rank} {c.course_county}
                          </span>
                        )}
                      </div>
                    </div>

                {(c.hasCourse || c.course_county) && (
                  <div className="mt-3 grid grid-cols-3 gap-2 bg-white/70 rounded-xl border border-slate-200 p-2">
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-base font-extrabold text-slate-900 leading-none">
                        {c.number_holes || '?'}
                      </div>
                      <div className="text-xxs font-semibold text-slate-500 mt-1">
                        Holes
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-base font-extrabold text-slate-900 leading-none">
                        {c.club_formation || '?'}
                      </div>
                      <div className="text-xxs font-semibold text-slate-500 mt-1">
                        Formed
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      {c.course_county && countyFlagUrl(c.course_county) ? (
                        <img
                          src={countyFlagUrl(c.course_county)}
                          alt=""
                          width="34"
                          height="22"
                          className="rounded shadow-sm"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-[22px]" />
                      )}
                      <div className="text-xxs font-semibold text-slate-500 mt-1">
                        {c.course_county || 'County'}
                      </div>
                    </div>
                  </div>
                )}

                    <div className="mt-3 flex flex-wrap gap-2">
                      {c.architect && (
                        <span className="text-xxs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {c.architect}
                        </span>
                      )}
                      {c.course_style && (
                        <span className="text-xxs px-2 py-1 rounded-full bg-slate-50 text-slate-700 border border-slate-200">
                          {c.course_style}
                        </span>
                      )}
                      {typeof c.weekend_green_fee === 'number' && c.weekend_green_fee > 0 && (
                        <span className="text-xxs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                          From £{Math.round(c.weekend_green_fee).toLocaleString()}
                        </span>
                      )}
                    </div>
                      </Wrapper>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

